import { useState, useCallback, useMemo } from 'react';
import { Calculator, Check, BookOpen, ListChecks, FlaskConical } from 'lucide-react';
import InfoBox from '../InfoBox';

const steps = [
  { id: 1, title: 'Review Spectral Data', short: 'Verify your wavelength measurements' },
  { id: 2, title: 'Calculate Rₕ Values', short: 'Apply the Rydberg formula to each line' },
  { id: 3, title: 'Calculate Average & Error', short: 'Determine percent error vs accepted value' }
];

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Reviewing Spectral Data',
    content: 'Before calculating the Rydberg constant, verify your wavelength measurements. Small errors in angle reading translate to wavelength errors. The prism spectrometer uses the dispersion properties of glass, where different wavelengths refract at different angles.'
  },
  2: {
    title: 'The Rydberg Formula',
    content: 'The Rydberg formula predicts hydrogen spectral lines: 1/λ = Rₕ(1/n₁\u00B2 - 1/n₂\u00B2). For the Balmer series, n₁=2 and n₂=3,4,5,6... This formula was empirical until Bohr explained it using quantized electron orbits, marking a triumph of early quantum theory.'
  },
  3: {
    title: 'Calculating Percent Error',
    content: 'The accepted value of Rₕ is 1.09678 × 10⁷ m⁻\u00B9. Percent error measures how close your experimental value is to the accepted value: % error = |experimental − accepted|/accepted × 100. Also calculate % error between your calculated wavelengths for hydrogen and the actual values.'
  }
};

interface Part3CalculationProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
  recordedLines?: Array<{
    name: string;
    transition: string;
    wavelength: number;
  }>;
}

// Balmer series data
const BALMER_DATA = [
  { name: 'Red', nUpper: 3, nLower: 2, wavelength: 656.3 },
  { name: 'Blue', nUpper: 4, nLower: 2, wavelength: 486.1 },
  { name: 'Purple', nUpper: 5, nLower: 2, wavelength: 434.0 },
  { name: 'Purple (weak)', nUpper: 6, nLower: 2, wavelength: 410.2 }
];

const ACCEPTED_RH = 1.09678e7; // Rydberg constant in m^-1

export default function Part3Calculation({ onStepComplete, currentStep = 1, recordedLines }: Part3CalculationProps) {
  const [dataReviewed, setDataReviewed] = useState(false);
  const [calculatedRhValues, setCalculatedRhValues] = useState<number[]>([]);
  const [averageRh, setAverageRh] = useState<number | null>(null);
  const [percentError, setPercentError] = useState<number | null>(null);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const stepDone = [dataReviewed, calculatedRhValues.length > 0, percentError !== null];
  const completedCount = stepDone.filter(Boolean).length;
  const activeStep = completedCount < steps.length ? completedCount + 1 : steps.length;

  // Use recorded lines or default Balmer data
  const lines = recordedLines && recordedLines.length >= 4 ? recordedLines : BALMER_DATA;

  // Calculate Rh from wavelength: 1/lambda = Rh * (1/nLower^2 - 1/nUpper^2)
  const calculateRh = useCallback((wavelengthNm: number, nUpper: number, nLower: number): number => {
    const lambdaM = wavelengthNm * 1e-9; // Convert nm to m
    const invLambda = 1 / lambdaM;
    const term = (1 / (nLower * nLower)) - (1 / (nUpper * nUpper));
    return invLambda / term;
  }, []);

  const handleReviewData = useCallback(() => {
    setDataReviewed(true);
    if (onStepComplete) onStepComplete('calculation-1');
  }, [onStepComplete]);

  const handleCalculateRydberg = useCallback(() => {
    const rhValues = BALMER_DATA.map((line, i) => {
      // Use recorded wavelength if available, otherwise use theoretical
      const wavelength = lines[i]?.wavelength || line.wavelength;
      return calculateRh(wavelength, line.nUpper, line.nLower);
    });

    setCalculatedRhValues(rhValues);
    if (onStepComplete) onStepComplete('calculation-2');
  }, [lines, calculateRh, onStepComplete]);

  const handleCalculateError = useCallback(() => {
    if (calculatedRhValues.length > 0) {
      const avg = calculatedRhValues.reduce((a, b) => a + b, 0) / calculatedRhValues.length;
      const error = Math.abs((avg - ACCEPTED_RH) / ACCEPTED_RH) * 100;

      setAverageRh(avg);
      setPercentError(error);
      if (onStepComplete) onStepComplete('calculation-3');
    }
  }, [calculatedRhValues, onStepComplete]);

  const isComplete = percentError !== null;

  return (
    <div className="exp1-part-layout">
      {/* Steps Panel */}
      <div className="exp1-steps-panel">
        <div className="exp1-steps-header">
          <ListChecks size={16} />
          <h3>Part III: Calculation</h3>
        </div>
        <div className="exp1-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp1-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp1-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp1-step-content">
                  <span className={`exp1-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                  {active && !done && <span className="exp1-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp1-lab-bench">
        <div className="exp1-bench-header">
          <div className="exp1-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp1-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp1-bench-area">
      <div className="calculation-lab">
        {/* Rydberg Formula */}
        <div className="formula-panel">
          <h4>Rydberg Formula</h4>
          <div className="formula">
            <span className="formula-large">
              1/λ = R<sub>H</sub> × (1/n<sub>1</sub><sup>2</sup> − 1/n<sub>2</sub><sup>2</sup>)
            </span>
          </div>
          <p className="formula-note">
            For Balmer series: n<sub>1</sub> = 2, n<sub>2</sub> = 3, 4, 5, 6...
          </p>
        </div>

        {/* Data Table */}
        <div className="data-table">
          <h4>Wavelength Data</h4>
          <table>
            <thead>
              <tr>
                <th>Line</th>
                <th>Transition</th>
                <th>λ (nm)</th>
                <th>1/n<sub>1</sub><sup>2</sup> − 1/n<sub>2</sub><sup>2</sup></th>
                <th>R<sub>H</sub> (m<sup>−1</sup>)</th>
              </tr>
            </thead>
            <tbody>
              {BALMER_DATA.map((line, i) => {
                const wavelength = lines[i]?.wavelength || line.wavelength;
                const term = (1 / (line.nLower * line.nLower)) - (1 / (line.nUpper * line.nUpper));
                const rh = calculatedRhValues[i];

                return (
                  <tr key={line.name}>
                    <td>{line.name}</td>
                    <td>{line.nUpper} → {line.nLower}</td>
                    <td>{wavelength.toFixed(1)}</td>
                    <td>{term.toFixed(5)}</td>
                    <td className="rh-value">
                      {rh ? rh.toExponential(3) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {!dataReviewed && (
            <div className="action-row">
              <button className="action-btn" onClick={handleReviewData}>
                <BookOpen size={16} />
                Review Data
              </button>
              {stepInfoContent[1] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                >
                  ?
                </button>
              )}
            </div>
          )}
          {dataReviewed && calculatedRhValues.length === 0 && (
            <div className="action-row">
              <button className="action-btn" onClick={handleCalculateRydberg}>
                <Calculator size={16} />
                Calculate R<sub>H</sub> Values
              </button>
              {stepInfoContent[2] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
                >
                  ?
                </button>
              )}
            </div>
          )}
          {calculatedRhValues.length > 0 && !percentError && (
            <div className="action-row">
              <button className="action-btn success" onClick={handleCalculateError}>
                <Check size={16} />
                Calculate Average & Error
              </button>
              {stepInfoContent[3] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(3); }}
                >
                  ?
                </button>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        {isComplete && (
          <div className="results-panel">
            <h4>Results</h4>
            <div className="results-grid">
              <div className="result-item">
                <span className="result-label">Your Average R<sub>H</sub></span>
                <span className="result-value">{averageRh?.toExponential(3)} m<sup>−1</sup></span>
              </div>
              <div className="result-item">
                <span className="result-label">Accepted R<sub>H</sub></span>
                <span className="result-value">{ACCEPTED_RH.toExponential(3)} m<sup>−1</sup></span>
              </div>
              <div className="result-item">
                <span className="result-label">Percent Error</span>
                <span className={`result-value ${percentError! < 5 ? 'good' : percentError! < 10 ? 'ok' : 'warning'}`}>
                  {percentError?.toFixed(2)}%
                </span>
              </div>
            </div>

            <div className={`accuracy-badge ${percentError! < 5 ? 'excellent' : percentError! < 10 ? 'good' : 'acceptable'}`}>
              {percentError! < 5 ? 'Excellent accuracy!' :
                percentError! < 10 ? 'Good accuracy' : 'Acceptable - consider sources of error'}
            </div>

            <div className="significance-box">
              <h5>Physical Significance</h5>
              <p>
                The Rydberg constant R<sub>H</sub> = 1.09678 × 10<sup>7</sup> m<sup>−1</sup> appears in the
                Rydberg formula that predicts hydrogen spectral lines. It can be derived from fundamental
                constants:
              </p>
              <div className="derived-formula">
                R<sub>H</sub> = m<sub>e</sub>e<sup>4</sup> / (8ε<sub>0</sub><sup>2</sup>h<sup>3</sup>c)
              </div>
              <p>
                Where m<sub>e</sub> is electron mass, e is electron charge, h is Planck's constant,
                and c is the speed of light.
              </p>
            </div>
          </div>
        )}

        {/* Sample Calculation */}
        {dataReviewed && calculatedRhValues.length === 0 && (
          <div className="sample-calc">
            <h4>Sample Calculation (H-alpha)</h4>
            <div className="calc-steps">
              <p>Given: λ = 656.3 nm = 656.3 × 10<sup>−9</sup> m</p>
              <p>n<sub>1</sub> = 2, n<sub>2</sub> = 3</p>
              <p>1/n<sub>1</sub><sup>2</sup> − 1/n<sub>2</sub><sup>2</sup> = 1/4 − 1/9 = 0.1389</p>
              <p>R<sub>H</sub> = (1/λ) / 0.1389</p>
              <p>R<sub>H</sub> = (1.524 × 10<sup>6</sup>) / 0.1389</p>
              <p className="result">R<sub>H</sub> = 1.09678 × 10<sup>7</sup> m<sup>−1</sup></p>
            </div>
          </div>
        )}

        <style>{`
          .exp1-part-layout { display: flex; gap: 16px; flex: 1; width: 100%; min-height: 0; padding: 16px; }
          .exp1-steps-panel { width: 240px; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
          .exp1-steps-header { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); padding: 14px 16px; display: flex; align-items: center; gap: 10px; color: white; }
          .exp1-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp1-step-item { display: flex; align-items: flex-start; gap: 12px; padding: 8px 10px; border-radius: 8px; margin-bottom: 2px; }
          .exp1-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp1-step-number { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; background: var(--border); color: var(--text-muted); }
          .exp1-step-number.done { background: var(--secondary); color: white; }
          .exp1-step-number.current { background: var(--primary); color: white; }
          .exp1-step-content { flex: 1; min-width: 0; }
          .exp1-step-title { font-size: 12px; font-weight: 600; display: block; }
          .exp1-step-title.highlight { color: var(--primary-light); }
          .exp1-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp1-lab-bench { flex: 1; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: auto; }
          .exp1-bench-header { background: linear-gradient(135deg, var(--secondary), var(--secondary-dark)); padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; color: white; }
          .exp1-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp1-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp1-bench-area { flex: 1; padding: 20px; overflow-y: auto; }

          .calculation-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            max-width: 700px;
          }

          .formula-panel {
            width: 100%;
            background: var(--bg-elevated);
            padding: 20px;
            border-radius: 12px;
            text-align: center;
          }

          .formula-panel h4 {
            margin: 0 0 12px 0;
            font-size: 14px;
            color: var(--text-secondary);
          }

          .formula {
            background: var(--bg-dark);
            padding: 16px;
            border-radius: 8px;
            margin-bottom: 12px;
          }

          .formula-large {
            font-size: 18px;
            font-family: 'Times New Roman', serif;
            color: var(--text-primary);
          }

          .formula-note {
            margin: 0;
            font-size: 12px;
            color: var(--text-muted);
          }

          .data-table {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
          }

          .data-table h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          th, td {
            padding: 10px 8px;
            text-align: center;
            border: 1px solid var(--border);
          }

          th {
            background: var(--bg-dark);
            color: var(--text-secondary);
            font-weight: 600;
          }

          td {
            color: var(--text-primary);
          }

          .rh-value {
            font-family: monospace;
            color: #00A86B;
          }

          .action-buttons {
            display: flex;
            gap: 12px;
          }

          .action-row {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .results-panel {
            width: 100%;
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid #00A86B;
            padding: 20px;
            border-radius: 12px;
          }

          .results-panel h4 {
            margin: 0 0 16px 0;
            font-size: 14px;
            color: #00A86B;
          }

          .results-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 16px;
          }

          .result-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .result-label {
            font-size: 11px;
            color: var(--text-muted);
          }

          .result-value {
            font-size: 14px;
            font-family: monospace;
            font-weight: 600;
            color: var(--text-primary);
          }

          .result-value.good {
            color: #00A86B;
          }

          .result-value.ok {
            color: #FFC107;
          }

          .result-value.warning {
            color: #FF5A36;
          }

          .accuracy-badge {
            text-align: center;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            margin-bottom: 16px;
          }

          .accuracy-badge.excellent {
            background: rgba(0, 168, 107, 0.2);
            color: #00A86B;
          }

          .accuracy-badge.good {
            background: rgba(255, 193, 7, 0.2);
            color: #FFC107;
          }

          .accuracy-badge.acceptable {
            background: rgba(255, 90, 54, 0.2);
            color: #FF5A36;
          }

          .significance-box {
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 8px;
          }

          .significance-box h5 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .significance-box p {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .derived-formula {
            text-align: center;
            padding: 8px;
            background: var(--bg-dark);
            border-radius: 4px;
            font-family: 'Times New Roman', serif;
            font-size: 13px;
            margin: 8px 0;
          }

          .sample-calc {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
          }

          .sample-calc h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .calc-steps {
            font-size: 12px;
            font-family: 'Times New Roman', serif;
            color: var(--text-primary);
            line-height: 2;
          }

          .calc-steps .result {
            font-weight: 600;
            color: #00A86B;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp1"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
